import * as Sentry from '@sentry/react';
import {
  IonIcon,
  IonLabel,
  IonRouterOutlet,
  IonSpinner,
  IonTabBar,
  IonTabButton,
  IonTabs,
  useIonToast,
} from '@ionic/react';
import { ellipseOutline, list, map, eye, barChartOutline, documentTextOutline } from 'ionicons/icons';
import { Redirect, Route, useLocation } from 'react-router';
import { IValueDidChange, reaction, toJS } from 'mobx';
import { observer } from 'mobx-react-lite';
import React, { useEffect } from 'react';
import { useStores } from './models';
import { AuthUser } from './models/User';
import LoginPage from './pages/LoginPage';
import OnBoarding from './pages/OnBoarding';
import Profile from './pages/Profile';
import RecordPage from './pages/Record';
import SharedRunPage from './pages/SharedRun';
import PublicRuns from './pages/PublicRunsPage';
import { RunPreview } from './pages/ViewRunPage';
import RunDetailTab from './pages/ViewRunPage/RunDetailTab';
import { useAuthService } from './services/Authentication';
import { useFSM } from './services/DongleController/DongleController';

import MapPage from 'pages/MapPage';
import ExplorePage from 'pages/ExplorePage';
import ControlPage from './pages/ControlPage';
import { MESSAGE_ALIVE_MS, MESSAGE_POSITION } from 'util/constants';
import Trans from 'util/Trans';
import DocsPage from './pages/DocsPage';
import StatsPage from './pages/StatsPage';

// Only import MinimumScreenSize components for web version
let MinimumScreenSize: React.ComponentType | null = null;
let useScreenSize: (() => { isMinimumSize: boolean }) | null = null;

if (process.env.REACT_APP_IS_WEB === 'true') {
  MinimumScreenSize = require('./components/MinimumScreenSize').default;
  useScreenSize = require('./hooks/useScreenSize').default;
}

export interface UserRequired {
  user: AuthUser;
}
export type StatesBLE = 'DISCONNECTED' | 'CONNECTED' | 'CONNECTING' | 'ERROR';

const Navigation: React.FC = observer(() => {
  const AuthService = useAuthService();
  const store = useStores();
  const fsm = useFSM();
  const [toast, dismiss] = useIonToast();
  const location = useLocation();
  const [present] = useIonToast();
  
  // Check if running as web app and if screen size meets minimum requirements
  const isWeb = process.env.REACT_APP_IS_WEB === 'true';
  const screenSize = isWeb && useScreenSize ? useScreenSize() : { isMinimumSize: true };
  const shouldShowMinimumScreen = isWeb && !screenSize.isMinimumSize;

  useEffect(() => {
    const subscription = fsm.connectionLost.subscribe(() => {
      present({
        message: 'Error while recording',
        duration: MESSAGE_ALIVE_MS,
        color: 'danger',
        position: MESSAGE_POSITION,
      });
    });

    return () => subscription.unsubscribe();
  }, [fsm, toast, dismiss, present]);

  // Most routes need a user object to work properly.
  // We check on the Navigation Level if a user is logged in
  // and also listen to changes to the user object.
  // If no user is logged in, we render the login page
  useEffect(() => {
    const disposer = reaction(
      () => AuthService.user.get(),
      (usr) => {
        Sentry.setTags({
          'fb.uid': usr?.uid,
        });
        if (usr) store.setUser(toJS(usr), AuthService.isNewUser);
        else store.setUser(null, false);
      }
    );
    AuthService.getCurrentUser();
    return disposer;
  }, [AuthService, store]);

  const { knownFreak } = useStores();
  useEffect(() => {
    if (!knownFreak) return;
    if (knownFreak.entry?.id && knownFreak.autoConnect) {
      fsm.deviceId.set(knownFreak.entry.id);
      fsm.connectToSensor();
    }
  }, [fsm, knownFreak]);

  // if (store.isUserLoading) {
  //   return (
  //     <div
  //       style={{
  //         display: 'flex',
  //         justifyContent: 'center',
  //         alignItems: 'center',
  //         height: '100vh',
  //       }}
  //     >
  //       <IonSpinner name="crescent" style={{ width: 48, height: 48 }} />
  //     </div>
  //   );
  // }

  if (!store.currentUser) {
    return <LoginPage onLogIn={(user, isNew) => {
      // For web app, mark onboarding as completed on login
      if (process.env.REACT_APP_IS_WEB === 'true') {
        store.appData.completeOnboarding();
      }
    }} />;
  }

  // Skip onboarding for web app and go directly to profile
  if (!store.appData.onboardingCompleted && process.env.REACT_APP_IS_WEB !== 'true') {
    return <OnBoarding />;
  }

  // Show minimum screen size warning for web users with small screens
  if (shouldShowMinimumScreen && MinimumScreenSize) {
    return <MinimumScreenSize />;
  }

  // Hide the tab bar on the trick detail page
  const hideTabBar =
    location.pathname.startsWith('/trick/') ||
    location.pathname.startsWith('/trickpreview/') ||
    (!isWeb && location.pathname.startsWith('/public/'));

  return (
    <IonTabs>
      <IonRouterOutlet className="notchSafe">
        <Route path="/shared/:id">
          <SharedRunPage />
        </Route>
        <Route exact path="/:tab(profile)">
          <Profile />
        </Route>
        <Route exact path="/:tab(profile)/:id">
          <Profile />
        </Route>
        <Route exact path="/:tab(trickList)">
          <PublicRuns />
        </Route>
        <Route exact path="/:tab(public)/:id">
          <PublicRuns />
        </Route>
        <Route exact path="/:tab(record)">
          <RecordPage />
        </Route>
        <Route exact path="/:tab(explore)">
          <ExplorePage />
        </Route>
        <Route path="/onboarding">
          <OnBoarding />
        </Route>
        <Route path="/map">
          <MapPage />
        </Route>
        <Route path="/trickpreview/:id">
          <RunPreview />
        </Route>
        <Route exact path="/:tab(trick)/:id">
          <RunDetailTab />
        </Route>
        <Route exact path="/">
          <Redirect to={process.env.REACT_APP_IS_WEB === 'true' ? "/profile" : "/record"} />
        </Route>
        <Route exact path="/control">
          <ControlPage />
        </Route>
        <Route path="/docs">
          <DocsPage />
        </Route>
        <Route path="/stats">
          <StatsPage />
        </Route>
        <Route render={() => <Redirect to="/" />} />
      </IonRouterOutlet>

      <IonTabBar
        style={{
          '--color': 'var(--ion-color-light)',
          '--background': 'var(--ion-color-secondary)',
          background: 'var(--ion-color-secondary)',
        }}
        className={`${hideTabBar ? 'ion-hide' : ''}`}
        slot={isWeb ? 'top' : 'bottom'}
      >
        <IonTabButton tab="explore" href="/explore">
          <IonIcon size="large" src="/assets/icons/Friends.svg" />
          <IonLabel>
            <Trans i18nKey="TAB_EXPLORE">Explore</Trans>
          </IonLabel>
        </IonTabButton>

        <IonTabButton tab="map" href="/map">
          <IonIcon size="large" icon={map} />
          <IonLabel>
            <Trans i18nKey="TAB_MAP">Map</Trans>
          </IonLabel>
        </IonTabButton>

        {process.env.REACT_APP_IS_WEB !== 'true' && (
          <IonTabButton tab="record" href="/record">
            <IonIcon size="large" icon={ellipseOutline} />
            <IonLabel>
              <Trans i18nKey="TAB_RECORD">Record</Trans>
            </IonLabel>
          </IonTabButton>
        )}

        <IonTabButton tab="trickList" href="/trickList">
          <IonIcon size="large" icon={eye} />
          <IonLabel>
            <Trans i18nKey="TAB_FEED">Tricks</Trans>
          </IonLabel>
        </IonTabButton>

        <IonTabButton tab="profile" href="/profile">
          <IonIcon size="large" src="/assets/icons/Person_Spike.svg" />
          <IonLabel>
            <Trans i18nKey="TAB_PROFILE">Profile</Trans>
          </IonLabel>
        </IonTabButton>
        {isWeb && (
          <IonTabButton tab="stats" href="/stats">
            <IonIcon size="large" icon={barChartOutline} />
            <IonLabel>Stats</IonLabel>
          </IonTabButton>
        )}
        {isWeb && (
          <IonTabButton tab="docs" href="/docs">
            <IonIcon size="large" icon={documentTextOutline} />
            <IonLabel>Docs</IonLabel>
          </IonTabButton>
        )}
      </IonTabBar>
    </IonTabs>
  );
});

export default Navigation;
